/**
 * Users (plan §18 "Users"): create/edit/disable, role and bot grants,
 * password reset (temporary password through a protected field), MFA status
 * and explicit MFA recovery. Administrators cannot see or retrieve passwords.
 */
import { useState } from "react";
import {
  Badge, Button, Group, Modal, MultiSelect, Paper, PasswordInput, Select, Stack, Switch, Table, Text, TextInput, Textarea, Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { fleetApi, type UserRow } from "../../api/fleet";

export function UsersPage() {
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ["users"], queryFn: () => fleetApi.users.list() });
  const bots = useQuery({ queryKey: ["fleet", "agents"], queryFn: () => fleetApi.agents.list() });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["users"] });
  const toast = (title: string, err?: unknown) => notifications.show({ color: err ? "red" : "green", title, message: err ? (err as Error).message : "" });

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ email: "", role: "viewer" as "admin" | "viewer", temp_password: "", display_name: "", grants: [] as string[] });
  const create = useMutation({
    mutationFn: () => fleetApi.users.create({ ...form, grants: form.role === "viewer" ? form.grants : undefined }),
    onSuccess: () => { toast("User created (temporary password; setup required at first login)"); setCreateOpen(false); setForm({ email: "", role: "viewer", temp_password: "", display_name: "", grants: [] }); refresh(); },
    onError: (e) => toast("Create failed", e),
  });

  const [editing, setEditing] = useState<UserRow | null>(null);
  const [editGrants, setEditGrants] = useState<string[]>([]);
  const update = useMutation({
    mutationFn: (body: Parameters<typeof fleetApi.users.update>[1]) => fleetApi.users.update(editing!.id, body),
    onSuccess: () => { toast("User updated (access revision bumped where required)"); refresh(); },
    onError: (e) => toast("Update failed", e),
  });

  const [resetFor, setResetFor] = useState<UserRow | null>(null);
  const [resetPw, setResetPw] = useState("");
  const [resetReason, setResetReason] = useState("");
  const reset = useMutation({
    mutationFn: () => fleetApi.users.resetPassword(resetFor!.id, { temp_password: resetPw, reason: resetReason || undefined }),
    onSuccess: () => { toast("Password reset: sessions revoked, change required at next login, MFA unchanged"); setResetFor(null); setResetPw(""); setResetReason(""); refresh(); },
    onError: (e) => toast("Reset failed", e),
  });

  const [mfaFor, setMfaFor] = useState<UserRow | null>(null);
  const [mfaReason, setMfaReason] = useState("");
  const mfaRecovery = useMutation({
    mutationFn: () => fleetApi.users.mfaRecovery(mfaFor!.id, mfaReason),
    onSuccess: () => { toast("MFA recovery recorded: authenticator removed, re-enrolment required"); setMfaFor(null); setMfaReason(""); refresh(); },
    onError: (e) => toast("MFA recovery failed", e),
  });

  const botOptions = (bots.data ?? []).map((b) => ({ value: b.id, label: b.display_name }));

  return (
    <>
      <Group justify="space-between" mb="lg">
        <Title order={2}>Users</Title>
        <Button onClick={() => setCreateOpen(true)}>Create user</Button>
      </Group>
      <Paper withBorder p="md">
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr><Table.Th>Email</Table.Th><Table.Th>Name</Table.Th><Table.Th>Role</Table.Th><Table.Th>Status</Table.Th><Table.Th>Setup</Table.Th><Table.Th>MFA</Table.Th><Table.Th>Grants</Table.Th><Table.Th>Last login</Table.Th><Table.Th /></Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(users.data ?? []).map((u) => (
              <Table.Tr key={u.id}>
                <Table.Td>{u.email}</Table.Td>
                <Table.Td>{u.display_name ?? <Text c="dimmed" span>—</Text>}</Table.Td>
                <Table.Td><Badge color={u.role === "admin" ? "grape" : "blue"}>{u.role}</Badge></Table.Td>
                <Table.Td><Badge color={u.status === "active" ? "green" : "gray"}>{u.status}</Badge></Table.Td>
                <Table.Td>{u.must_change_password || !u.profile_completed ? <Badge color="yellow">pending</Badge> : <Badge color="green">done</Badge>}</Table.Td>
                <Table.Td>{u.mfa_enabled ? <Badge color="green">enabled</Badge> : <Badge color={u.role === "admin" ? "red" : "gray"}>{u.role === "admin" ? "required" : "off"}</Badge>}</Table.Td>
                <Table.Td>{u.grants === "all" ? "all bots" : `${u.grants.length} bot(s)`}</Table.Td>
                <Table.Td>{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "never"}</Table.Td>
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    <Button size="compact-xs" variant="light" onClick={() => { setEditing(u); setEditGrants(u.grants === "all" ? [] : u.grants); }}>Edit</Button>
                    <Button size="compact-xs" variant="light" color="orange" onClick={() => setResetFor(u)}>Reset password</Button>
                    <Button size="compact-xs" variant="light" color="red" onClick={() => setMfaFor(u)} disabled={!u.mfa_enabled}>MFA recovery</Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      <Modal opened={createOpen} onClose={() => setCreateOpen(false)} title="Create user">
        <Stack>
          <TextInput label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.currentTarget.value })} required />
          <TextInput label="Display name (optional)" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.currentTarget.value })} />
          <Select label="Role" data={[{ value: "viewer", label: "Viewer (read-only, granted bots)" }, { value: "admin", label: "Administrator" }]} value={form.role} onChange={(v) => setForm({ ...form, role: (v as "admin" | "viewer") ?? "viewer" })} />
          {form.role === "viewer" && <MultiSelect label="Bot grants" data={botOptions} value={form.grants} onChange={(v) => setForm({ ...form, grants: v })} searchable />}
          <PasswordInput label="Temporary password" description="Shared out-of-band. The user must replace it at first login; it is never stored or shown again." value={form.temp_password} onChange={(e) => setForm({ ...form, temp_password: e.currentTarget.value })} required />
          <Button onClick={() => create.mutate()} loading={create.isPending}>Create</Button>
        </Stack>
      </Modal>

      <Modal opened={!!editing} onClose={() => setEditing(null)} title={`Edit ${editing?.email ?? ""}`}>
        {editing && (
          <Stack>
            <Select label="Role" data={[{ value: "viewer", label: "Viewer" }, { value: "admin", label: "Administrator" }]} value={editing.role} onChange={(v) => v && update.mutate({ role: v as "admin" | "viewer" })} />
            <Switch label="Active" checked={editing.status === "active"} onChange={(e) => update.mutate({ status: e.currentTarget.checked ? "active" : "disabled" })} description="Disabling revokes every session immediately. The last usable administrator cannot be disabled." />
            {editing.role === "viewer" && (
              <>
                <MultiSelect label="Bot grants" data={botOptions} value={editGrants} onChange={setEditGrants} searchable />
                <Button variant="light" onClick={() => update.mutate({ grants: editGrants })}>Save grants (updates active streams)</Button>
              </>
            )}
          </Stack>
        )}
      </Modal>

      <Modal opened={!!resetFor} onClose={() => setResetFor(null)} title={`Reset password for ${resetFor?.email ?? ""}`}>
        <Stack>
          <Text size="sm" c="dimmed">All sessions are revoked and the user must choose a new password at next login. MFA is NOT changed by this action.</Text>
          <PasswordInput label="Temporary password" value={resetPw} onChange={(e) => setResetPw(e.currentTarget.value)} required />
          <Textarea label="Reason (audited)" value={resetReason} onChange={(e) => setResetReason(e.currentTarget.value)} />
          <Button color="orange" onClick={() => reset.mutate()} loading={reset.isPending}>Reset</Button>
        </Stack>
      </Modal>

      <Modal opened={!!mfaFor} onClose={() => setMfaFor(null)} title={`MFA recovery for ${mfaFor?.email ?? ""}`}>
        <Stack>
          <Text size="sm" c="dimmed">Separate, explicitly confirmed operation: removes the authenticator and recovery codes, revokes sessions, and forces re-enrolment. The password is not changed.</Text>
          <Textarea label="Reason (required, audited)" value={mfaReason} onChange={(e) => setMfaReason(e.currentTarget.value)} required />
          <Button color="red" onClick={() => mfaRecovery.mutate()} loading={mfaRecovery.isPending} disabled={mfaReason.trim().length < 3}>Disable MFA for this user</Button>
        </Stack>
      </Modal>
    </>
  );
}
